import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { achImageUrl } from "@/features/achievements/lib/images";
import { useSiteWorkoutPagesHidden } from "@/features/accountNavV5/lib/useSiteWorkoutPagesHidden";
import { isTrainingHref } from "@/features/accountNavV5/model/siteWorkoutPages";
import { PlanHint } from "@/features/planReminder/ui/PlanHint";
import { APP_CLOSE_URL, isAppWindow } from "@/shared/lib/appWindow";
import { useOwnPointsHistory } from "@/shared/ui/OwnPointsLink/OwnPointsLink";
import { pointsRulesHref } from "../lib/rulesHref";
import { POINTS_RULES_ATTR, siteLangOf } from "../lib/chipAttrs";
import { myLevelsImgVersion, myPointsFor, prefetchMyPoints } from "../lib/myPoints";
import type { MineContent } from "../model/mine";
import {
    isPointsActionKey,
    POINTS_ACTION_SPHERE,
    pointsInfoContent,
    pointsTextLang,
    type PointsActionKey,
} from "../model/pointsActions";
import { isSamePage, pointsTodo, pointsTodoUsesWorkoutPages, type PointsTodo } from "../model/pointsTodo";
import { formatGain, isSphereKey, SphereKey, sphereVars } from "../model/spheres";
import cls from "./PointsInfoRoot.module.scss";

type Open = {
    sphere: SphereKey;
    label: string;
    action: string | null;
    ctx: string | null;
    amount: string | null;
    /** Чип, с которого открыли: закрыли окно — фокус обратно на него. */
    chip: HTMLElement;
    /** Чип стоит на экране «Как получить баллы» — кнопка «Все баллы» туда не нужна. */
    here: boolean;
    /** Баллы человека и уровень — то, что было загружено в момент нажатия (без «догрузки»). */
    mine: MineContent | null;
    /** Папка картинок уровня — вместе с mine. */
    imgVer: string | null;
};

/**
 * Текст чипа без aria-label: части в своих тегах (<b>+15 000</b><span>вклад</span>
 * в «Моём обучении») — через пробел, иначе «+15 000вклад»; соседние куски текста
 * («+» и «15 000») — слитно.
 */
const chipText = (el: Element): string =>
    Array.from(el.childNodes)
        .map((n) => (n.nodeType === 3 ? n.textContent || "" : ` ${n.textContent || ""} `))
        .join("");

const chipEl = (target: EventTarget | null): HTMLElement | null =>
    ((target as Element | null)?.closest?.("[data-points-chip]") as HTMLElement | null) || null;

/** Сфера окна: у знакомого действия — его сфера, иначе data-sphere чипа. */
const sphereOfChip = (sphere: SphereKey, action: string | null): SphereKey =>
    isPointsActionKey(action) ? POINTS_ACTION_SPHERE[action] : sphere;

/** Чип баллов, на который нажали (или Enter/пробел на нём), и что он говорит окну. */
const chipOf = (target: EventTarget | null, lang: string): Open | null => {
    const el = chipEl(target);
    const sphere = el?.getAttribute("data-sphere") || "";
    if (!el || !isSphereKey(sphere)) return null;
    const label = (el.getAttribute("aria-label") || chipText(el)).replace(/\s+/g, " ").trim();
    const action = el.getAttribute("data-points-action");
    return {
        sphere,
        label,
        action,
        ctx: el.getAttribute("data-points-ctx"),
        amount: el.getAttribute("data-points-amount"),
        chip: el,
        here: !!el.closest(`[${POINTS_RULES_ATTR}]`),
        mine: myPointsFor(sphereOfChip(sphere, action), lang),
        imgVer: myLevelsImgVersion(),
    };
};

/** «Составьте свой первый план» — его «Что сделать» уже ведёт в план: вторая такая же строка не нужна. */
const FIRST_PLAN_ACTION = "work.firstPlan";

/** Без нажатой клавиши-модификатора: ссылку открываем сами, а не новой вкладкой. */
const plainClick = (e: React.MouseEvent) => e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

type GoHandler = (href: string) => (e: React.MouseEvent) => void;

/**
 * «У вас 3 недели по плану · 16 250 баллов» в окне «Тренировок» — свои баллы: сами ссылка на
 * «Историю баллов» (владелец 12.10: нажать на цифру своих баллов — и туда, откуда бы ни было).
 * Объяснение — само это окно. Вход спрашиваем только в открытом окне «Тренировок»: окно одно на
 * весь сайт, и ради каждой страницы с чипами POINTS_HISTORY_FOR не нужен.
 */
const MineHistory = ({
    lang,
    onGo,
    children,
}: {
    lang: string;
    onGo: (to: string, state: { from: string }) => void;
    children: React.ReactNode;
}) => {
    const go = useOwnPointsHistory(lang);
    if (!go) return <>{children}</>;
    return (
        <a
            href={go.to}
            className={cls.mineLink}
            aria-label={go.label}
            title={go.label}
            data-testid="points-info-history"
            data-points-history=""
            onClick={(e) => {
                if (!plainClick(e)) return;
                e.preventDefault();
                onGo(go.to, go.state);
            }}
        >
            {children}
        </a>
    );
};

/**
 * «Что сделать»: строка и ссылки туда, где это делают. В окне приложения (?source=app)
 * ссылка на «Тренировки» сайта («Приложение MY REVIVE ›») возвращает в приложение
 * (APP_CLOSE_URL), как WorkoutsLink: календарь и план — там.
 */
const TodoView = ({ todo, go }: { todo: PointsTodo; go: GoHandler }) => {
    // Признак окна — при первом взгляде, как у WorkoutsLink.
    const [appWindow] = useState(isAppWindow);
    return (
        <div className={cls.todo} data-testid="points-info-todo">
            <h3 className={cls.todoTitle}>{todo.title}</h3>
            <p className={cls.todoText}>{todo.text}</p>
            {todo.links.length > 0 && (
                <div className={cls.todoLinks}>
                    {todo.links.map((l) => {
                        const toApp = appWindow && isTrainingHref(l.href);
                        return (
                            <a
                                key={l.key}
                                href={toApp ? APP_CLOSE_URL : l.href}
                                className={cls.todoLink}
                                data-go={l.key}
                                onClick={toApp ? undefined : go(l.href)}
                            >
                                {l.label}
                                <span className={cls.todoChev} aria-hidden="true">
                                    ›
                                </span>
                            </a>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

type TodoProps = {
    action: PointsActionKey;
    ctx: string | null;
    lang: string;
    here: { pathname: string; search?: string };
    go: GoHandler;
};

/**
 * Действие ведёт на календарь или план тренировок, а их скрыли с сайта (рубильник
 * SITE_WORKOUT_PAGES_HIDDEN, владелец 03.10) — ссылка на карточку приложения, как у
 * калитки этих адресов. Спрашиваем рубильник только у таких действий и только в открытом
 * окне: список админов не грузится ради каждого чипа.
 */
const TodoWithWorkoutSwitch = ({ action, ctx, lang, here, go }: TodoProps) => {
    const { hidden, pending } = useSiteWorkoutPagesHidden();
    return <TodoView todo={pointsTodo(action, ctx, lang, here, { workoutPagesHidden: hidden || pending })} go={go} />;
};

const PointsTodoBlock = (p: TodoProps) =>
    pointsTodoUsesWorkoutPages(p.action, p.ctx) ? (
        <TodoWithWorkoutSwitch {...p} />
    ) : (
        <TodoView todo={pointsTodo(p.action, p.ctx, p.lang, p.here)} go={p.go} />
    );

/**
 * Нажатие на любую строку, где даются баллы, — окно «за что эти баллы» (владелец
 * 28.09 и 03.10): сфера, за что, сколько, когда придут; баллы человека в этой сфере и
 * следующий уровень («сколько нужно для следующего уровня»); «Что сделать» — строка и
 * ссылки туда, где это делают (model/pointsTodo.ts); и кнопка «Все баллы: <сфера>» —
 * экран «Как получить баллы» раздела «Баллы» сразу на карточке этой сферы («назад» оттуда
 * возвращает на эту страницу). На самом этом экране кнопки нет — человек уже там. Что за
 * действие — говорит сам чип (data-points-action / -ctx / -amount,
 * model/pointsActions.ts); чип без действия — подпись чипа и фраза о сфере.
 *
 * Баллы и уровень — из того, что уже загружено (lib/myPoints.ts): окно подгружает их
 * заранее, когда на странице есть чипы или на чип навели, один раз на страницу. Не вошёл
 * или ещё не загрузилось — окно как раньше, без чисел; запоздавший ответ в открытое окно
 * не вставляется.
 *
 * Один на весь сайт: ловит нажатия на всё, что помечено data-points-chip (с
 * data-sphere), — общий PointsChip, чипы урока курса, «Моего обучения», диеты, умного
 * поиска, меню и строки «Как получить баллы». Нажатие на чип внутри ссылки или кнопки
 * открывает окно, а не саму ссылку.
 *
 * Слой — как у окна «за что» достижений (1350): выше листов курса («Подарить другу»,
 * план, отзыв — 1200) и окон MUI (1300). Esc закрывает только это окно, а не лист под
 * ним; Tab ходит по его кнопкам и ссылкам; закрыли — фокус на чипе. Язык — из адреса (ru,
 * en, de, es, cn; остальные — en), ссылки — на страницы того же языка.
 */
export const PointsInfoRoot = () => {
    const [open, setOpen] = useState<Open | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const lang = siteLangOf(location.pathname);
    const langRef = useRef(lang);
    langRef.current = lang;
    const sheetRef = useRef<HTMLDivElement>(null);
    const closeRef = useRef<HTMLButtonElement>(null);
    // Ушли со страницы по ссылке окна — фокус на старый чип не возвращаем.
    const leaving = useRef(false);

    useEffect(() => {
        const show = (e: Event) => {
            const hit = chipOf(e.target, langRef.current);
            if (!hit) return;
            e.preventDefault();
            e.stopPropagation();
            setOpen(hit);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") show(e);
        };
        // Навели, коснулись или дошли Tab до чипа — подгрузить баллы заранее (из кешей).
        const warm = (e: Event) => {
            if (chipEl(e.target)) void prefetchMyPoints(langRef.current);
        };
        document.addEventListener("click", show, true);
        document.addEventListener("keydown", onKey, true);
        document.addEventListener("pointerdown", warm, true);
        document.addEventListener("pointerover", warm, true);
        document.addEventListener("focusin", warm, true);
        return () => {
            document.removeEventListener("click", show, true);
            document.removeEventListener("keydown", onKey, true);
            document.removeEventListener("pointerdown", warm, true);
            document.removeEventListener("pointerover", warm, true);
            document.removeEventListener("focusin", warm, true);
        };
    }, []);

    // Переход — окно закрывается; на новой странице с чипами баллы подгружаются заранее
    // (на телефоне между касанием и нажатием времени на запрос нет).
    useEffect(() => {
        setOpen(null);
        const timers = [900, 3500].map((ms) =>
            window.setTimeout(() => {
                if (document.querySelector("[data-points-chip]")) void prefetchMyPoints(langRef.current);
            }, ms),
        );
        return () => timers.forEach((t) => window.clearTimeout(t));
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!open) return undefined;
        leaving.current = false;
        closeRef.current?.focus({ preventScroll: true });
        // Страница под окном не прокручивается. Лист курса под ним уже запер прокрутку —
        // тогда вернём как было; если лист за это время сам вернул прокрутку (страница
        // ушла), не трогаем.
        const body = document.body;
        const prevOverflow = body.style.overflow;
        body.style.overflow = "hidden";
        // Раньше всех (window, фаза захвата): Esc не доходит до листа или окна MUI под
        // этим окном, Tab не уходит из окна.
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setOpen(null);
            } else if (e.key === "Tab") {
                e.preventDefault();
                e.stopPropagation();
                const items = Array.from(sheetRef.current?.querySelectorAll<HTMLElement>("button, a[href]") || []);
                if (!items.length) return;
                const at = items.indexOf(document.activeElement as HTMLElement);
                const next = e.shiftKey ? (at <= 0 ? items.length - 1 : at - 1) : (at + 1) % items.length;
                items[next].focus();
            }
        };
        window.addEventListener("keydown", onKey, true);
        const chip = open.chip;
        return () => {
            window.removeEventListener("keydown", onKey, true);
            if (body.style.overflow === "hidden") body.style.overflow = prevOverflow;
            if (!leaving.current && chip.isConnected) chip.focus?.({ preventScroll: true });
        };
    }, [open]);

    if (!open || typeof document === "undefined") return null;
    // Сумма без своей записи на чипе — с разделителем языка страницы («+2,000» по-английски).
    const c = pointsInfoContent(open, lang, (n) => formatGain(n, lang));
    const rulesHref = pointsRulesHref(lang, c.sphere);
    const here = open.here || isSamePage(rulesHref, location);
    const mine = open.mine && open.mine.sphere === c.sphere ? open.mine : null;
    // Язык самих текстов окна (на /ar — английские), а не адреса: экранный диктор читает
    // их своим голосом.
    const textLang = pointsTextLang(lang);
    const close = () => setOpen(null);
    const go = (href: string) => (e: React.MouseEvent) => {
        if (!plainClick(e)) return;
        e.preventDefault();
        leaving.current = true;
        navigate(href);
    };

    return createPortal(
        <div
            className={cls.backdrop}
            onClick={(e) => {
                e.stopPropagation();
                close();
            }}
            data-testid="points-info"
        >
            <div
                ref={sheetRef}
                className={cls.sheet}
                role="dialog"
                aria-modal="true"
                aria-labelledby="points-info-title"
                aria-describedby="points-info-desc"
                lang={textLang === "cn" ? "zh" : textLang}
                style={sphereVars(c.sphere) as React.CSSProperties}
                data-sphere={c.sphere}
                data-action={open.action || undefined}
                onClick={(e) => e.stopPropagation()}
            >
                <span className={cls.handle} aria-hidden="true" />
                <p className={cls.kicker}>
                    <span className={cls.orb} aria-hidden="true" />
                    {c.kicker}
                </p>
                <h2 id="points-info-title" className={cls.title}>
                    {c.title}
                </h2>
                {c.amount && (
                    <p className={cls.amount} data-testid="points-info-amount">
                        <b>{c.amount}</b>
                        {c.unit && <span className={cls.unit}>{c.unit}</span>}
                    </p>
                )}
                <div id="points-info-desc" className={cls.lines}>
                    {c.lines.map((line) => (
                        <p key={line} className={cls.line}>
                            {line}
                        </p>
                    ))}
                </div>
                {mine && (
                    <div className={cls.mine} data-testid="points-info-mine" data-top={mine.top ? "" : undefined}>
                        {mine.img && open.imgVer && (
                            <img
                                className={cls.mineImg}
                                src={achImageUrl(mine.img, 96, open.imgVer)}
                                alt=""
                                width={48}
                                height={48}
                                loading="lazy"
                                decoding="async"
                                data-testid="points-info-level-img"
                            />
                        )}
                        <div className={cls.mineText}>
                            {(() => {
                                const lines = (
                                    <>
                                        <p className={cls.mineHave}>
                                            {mine.have} <b>{mine.value}</b> <span className={cls.mineOf}>{mine.of}</span>
                                        </p>
                                        {mine.points && (
                                            <p className={cls.minePoints} data-testid="points-info-points">
                                                {mine.points}
                                            </p>
                                        )}
                                    </>
                                );
                                return mine.sphere === "workouts" ? (
                                    <MineHistory
                                        lang={lang}
                                        onGo={(to, state) => {
                                            leaving.current = true;
                                            setOpen(null);
                                            navigate(to, { state });
                                        }}
                                    >
                                        {lines}
                                    </MineHistory>
                                ) : (
                                    lines
                                );
                            })()}
                            {mine.level && (
                                <p className={cls.mineLevel} data-testid="points-info-level">
                                    {mine.level}
                                </p>
                            )}
                        </div>
                    </div>
                )}
                {/* Плана нет — тренировки баллов не приносят (владелец 20.10): строка и «Составить план ›».
                    Есть план — ничего; у «Первого плана» то же уже сказано в «Что сделать». */}
                {c.sphere === "workouts" && open.action !== FIRST_PLAN_ACTION && (
                    <div
                        className={cls.planHint}
                        onClickCapture={(e) => {
                            if ((e.target as Element | null)?.closest?.("a[href]")) leaving.current = true;
                        }}
                    >
                        <PlanHint siteLang={lang} self variant="card" />
                    </div>
                )}
                {isPointsActionKey(open.action) && (
                    <PointsTodoBlock action={open.action} ctx={open.ctx} lang={lang} here={location} go={go} />
                )}
                <div className={cls.actions}>
                    {!here && (
                        <button
                            type="button"
                            className={cls.all}
                            data-testid="points-info-all"
                            data-href={rulesHref}
                            onClick={() => {
                                leaving.current = true;
                                navigate(rulesHref);
                            }}
                        >
                            {c.all} ›
                        </button>
                    )}
                    <button type="button" ref={closeRef} className={cls.close} onClick={close}>
                        {c.close}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};
